// src/services/companyService.js
import { repo } from "../dbservice/repo";
import { CompanyModel } from "../models/CompanyModel";

class CompanyService {
  constructor() {
    this.collectionName       = "companies";
    this.activitiesCollection = "companyActivities";
    this.unsubscribe          = null;
  }

  init() {}
  destroy() {}

  /* ─────────────────────────────────────────────────────────────────────
     SSR-safe current user getter
  ───────────────────────────────────────────────────────────────────── */
  get currentUser() {
    if (typeof window === "undefined") return null;
    try {
      const data = localStorage.getItem("userData");
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error("[CompanyService] Failed to parse userData", e);
      return null;
    }
  }

  _isAuthorized(currentUser) {
    const user = currentUser || this.currentUser;
    return Boolean(user && (user.uid || user.id || user.userId));
  }

  /* ─────────────────────────────────────────────────────────────────────
     READ
  ───────────────────────────────────────────────────────────────────── */
  async getAllCompanies(currentUser, limitVal = null) {
    const user = currentUser || this.currentUser;
    if (!this._isAuthorized(user) || user?.role !== "superadmin") {
      console.warn("⚠️ Unauthorized access: Superadmin required");
      return [];
    }
    return await repo.fetchData(this.collectionName, [], [], limitVal);
  }

  async getCompanyById(companyId) {
    if (!companyId) throw new Error("Company ID is required");
    try {
      return await repo.fetchById(this.collectionName, companyId);
    } catch (err) {
      console.error("[CompanyService] Failed to fetch company by ID", err);
      throw err;
    }
  }

   subscribeToCompanies(callback) {
    /* Convert any of the shapes we may encounter into epoch ms.
       Handles: string, number, Date, Firestore Timestamp ({seconds|toDate}). */
    const toEpoch = (v) => {
      if (v == null) return 0;
      if (typeof v === "number") return v;
      if (typeof v === "string") {
        const n = Date.parse(v);
        return Number.isNaN(n) ? 0 : n;
      }
      if (v instanceof Date) return v.getTime();
      if (typeof v?.toDate === "function") {
        try { return v.toDate().getTime(); } catch { /* fall through */ }
      }
      if (typeof v?.seconds === "number") return v.seconds * 1000;
      if (typeof v?._seconds === "number") return v._seconds * 1000;
      return 0;
    };

    return repo.subscribeToUpdates(
      this.collectionName,
      [],
      [],                       // ← NO server-side sort — return all docs
      null,
      (rows) => {
        const sorted = [...rows].sort(
          (a, b) =>
            toEpoch(b.metadata?.createdAt ?? b.createdAt) -
            toEpoch(a.metadata?.createdAt ?? a.createdAt),   // desc
        );
        callback(sorted);
      },
    );
  }
  /* ─────────────────────────────────────────────────────────────────────
     CREATE — routed through CompanyModel validation
     uniqueness check embedded here so the modal doesn't need to.
     Modal already does an optimistic client-side check; this is the
     server-side (well, service-side) authoritative gate.
  ───────────────────────────────────────────────────────────────────── */
  async createCompany(formData, currentUser) {
    const user = currentUser || this.currentUser;
    if (!this._isAuthorized(user)) throw new Error("Not authenticated");

       const targetId =
      formData.companyId ||
      formData.id ||
      (typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `C${Date.now()}${Math.random().toString(36).slice(2, 8)}`);

    /* reject duplicate company IDs at the service boundary */
    const existing = await repo.fetchById(this.collectionName, targetId).catch(() => null);
    if (existing) {
      throw new Error(
        `Company ID "${targetId}" already exists. Please use a unique identifier.`,
      );
    }

    const payload = {
      ...formData,
      id:        targetId,
      companyId: targetId,
      address:   formData.address1 || formData.address || "",
      assets:    formData.assets    ?? 0,
      userCount: formData.userCount ?? 0,
      status:    formData.status    || "Active",
      metadata: {
        ...(formData.metadata || {}),
        createdBy: user?.email || "system",
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(targetId, payload, {
      isCreation:  true,
      currentUser: user,
    });
  }

  /* ─────────────────────────────────────────────────────────────────────
     UPDATE — routed through CompanyModel validation
  ───────────────────────────────────────────────────────────────────── */
  async updateCompany(docId, updateData, currentUser) {
    const user = currentUser || this.currentUser;
    if (!this._isAuthorized(user)) throw new Error("Not authenticated");
    const isSuperAdmin = user?.role === "superadmin";
    if (!isSuperAdmin && docId !== user?.companyId) {
      throw new Error("Not authorized to edit this company.");
    }

    const safeUpdate = { ...updateData };
    if (!isSuperAdmin) {
      delete safeUpdate.name;
      delete safeUpdate.companyId;
      delete safeUpdate.status;
      delete safeUpdate.assets;
      delete safeUpdate.userCount;
      delete safeUpdate.metadata;   
    }

    const payload = {
      ...safeUpdate,
      id:        docId,
      companyId: safeUpdate.companyId || docId,
      address:   safeUpdate.address1 || safeUpdate.address || "",
      metadata: {
        ...(safeUpdate.metadata || {}),
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(docId, payload, {
      isCreation:  false,
      currentUser: user,
    });
  }
  /* ─────────────────────────────────────────────────────────────────────
     CORE PERSISTENCE ORCHESTRATOR
  ───────────────────────────────────────────────────────────────────── */
  async upsertData(targetId, data, { isCreation = false, currentUser } = {}) {
    if (!targetId) throw new Error("Target Company ID is required");

    const user = currentUser || this.currentUser;
    let finalData = { ...data };

    /* ── Read-before-write: preserve creation metadata ─────────────── */
    if (!isCreation) {
      const existing = await this.getCompanyById(targetId);
      if (existing) {
        finalData = {
          ...existing,
          ...data,
          metadata: {
            ...(existing.metadata || {}),
            ...(data.metadata || {}),
            createdAt:
              existing.metadata?.createdAt ||
              existing.createdAt ||
              new Date().toISOString(),
            createdBy:
              existing.metadata?.createdBy ||
              existing.createdBy ||
              "system",
          },
        };
      }
    }

    /* ── Model validation gate ─────────────────────────────────────── */
    const instance = new CompanyModel(
      { ...finalData, id: targetId, companyId: finalData.companyId || targetId },
      { isCreation },
    );

      if (!instance.isValid()) {
      const missing = [];
      if (!instance.name)        missing.push("name");
      if (!instance.companyId)   missing.push("companyId");
      if (!instance.address1)    missing.push("address1");
      if (!instance.countryCode) missing.push("countryCode");
      if (!instance.stateCode)   missing.push("stateCode");
      if (!instance.city)        missing.push("city");
      if (!instance.pincode)     missing.push("pincode");
      if (!instance.phoneNumber) missing.push("phoneNumber");
      throw new Error(
        `Invalid company payload. Missing or invalid: ${
          missing.join(", ") || "format check failed"
        }`,
      );
    }

         const payload = instance.toPayload();

    payload.id = targetId;
    payload.companyId = payload.companyId || targetId;

    const nowIso = new Date().toISOString();

    /* ── Ensure updatedAt/updatedBy always fresh on write ──────────── */
    payload.metadata = {
      ...(payload.metadata || {}),
      updatedAt: nowIso,
      updatedBy:
        data.metadata?.updatedBy ||
        payload.metadata?.updatedBy ||
        user?.email ||
        "system",
    };

    return await repo.upsertData(this.collectionName, targetId, payload);
  }

  /* ─────────────────────────────────────────────────────────────────────
     STATUS TOGGLE
  ───────────────────────────────────────────────────────────────────── */
  async toggleCompanyStatus(company, currentUser) {
    const user = currentUser || this.currentUser;
    if (!this._isAuthorized(user)) throw new Error("Not authenticated");

    const newStatus = company.status === "Active" ? "Inactive" : "Active";

    const merged = { ...company, status: newStatus };
    await this.upsertData(company.id, merged, {
      isCreation:  false,
      currentUser: user,
    });

    return newStatus;
  }

  /* ─────────────────────────────────────────────────────────────────────
     DELETE
  ───────────────────────────────────────────────────────────────────── */
  async deleteCompany(docId) {
    if (!docId) throw new Error("docId is required");
    return await repo.deleteData(this.collectionName, docId);
  }

  async bulkDeleteCompanies(ids) {
    if (!ids || ids.length === 0) return;
    await Promise.all(
      ids.map((id) => repo.deleteData(this.collectionName, id)),
    );
  }

  async bulkUpdateCompanyStatus(ids, status, currentUser) {
    if (!ids || ids.length === 0) return;
    const user = currentUser || this.currentUser;
    if (!this._isAuthorized(user)) throw new Error("Not authenticated");

    const nowIso = new Date().toISOString();
    await Promise.all(
      ids.map((id) =>
        repo.upsertData(this.collectionName, id, {
          status,
          metadata: {
            updatedAt: nowIso,
            updatedBy: user?.email || "unknown",
          },
        }),
      ),
    );
  }

  /* ─────────────────────────────────────────────────────────────────────
     METRIC SYNC
  ───────────────────────────────────────────────────────────────────── */
  async syncCompanyCounts(companyId, currentUser) {
    const user = currentUser || this.currentUser;
    if (!this._isAuthorized(user)) throw new Error("Not authenticated");

    const [assets, users] = await Promise.all([
      repo.fetchData("assets", [{ field: "companyId", value: companyId }]),
      repo.fetchData("users",  [{ field: "companyId", value: companyId }]),
    ]);

    await repo.upsertData(this.collectionName, companyId, {
      assets:    assets.length,
      userCount: users.length,
      metadata: {
        updatedAt: new Date().toISOString(),
        updatedBy: user?.email || "unknown",
      },
    });

    return { assets: assets.length, userCount: users.length };
  }

  /* ─────────────────────────────────────────────────────────────────────
     ACTIVITY LOG
  ───────────────────────────────────────────────────────────────────── */
  async logCompanyActivity(companyId, action, details, currentUser) {
    const user = currentUser || this.currentUser;
    if (!user?.email) return;

    const activityId = `act_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2)}`;

    await repo.upsertData(this.activitiesCollection, activityId, {
      companyId,
      action,
      details:     details || {},
      performedBy: user.email,
      timestamp:   new Date().toISOString(),
    });
  }

  /* ─────────────────────────────────────────────────────────────────────
     ROLE GUARD
  ───────────────────────────────────────────────────────────────────── */
  async assertSuperAdmin(currentUser) {
    const user = currentUser || this.currentUser;
    if (!this._isAuthorized(user)) throw new Error("Not authenticated");

    const userId  = user.uid || user.id || user.userId;
    const userDoc = await repo.fetchById("users", userId);

    if (!userDoc || userDoc.role !== "superadmin") {
      throw new Error(
        "Privileged Action Denied: Requires Super Administrator clearance.",
      );
    }
  }

  /* ─────────────────────────────────────────────────────────────────────
     UNIQUENESS CHECK (kept for consumers that need a sync check)
  ───────────────────────────────────────────────────────────────────── */
  checkCompanyIdExists(companyId, excludeDocId, allCompanies) {
    const normalized = (companyId || "").toLowerCase().trim();
    return (allCompanies || []).some(
      (c) =>
        (c.companyId || c.id || "").toLowerCase().trim() === normalized &&
        c.id !== excludeDocId,
    );
  }
}

export const companyService = new CompanyService();